import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import useSound from "use-sound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";

import Filter from "./Filter/Filter";
import Menu from "./Menu/Menu";
import TopCard from "./TopCard/TopCard";
import HoldingCard from "./HoldingCard/HoldingCard";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";

const backend = await new Backend().init();

export default function Holding() {
  const dispatch = useDispatch();
  const windowSize = useSize();
  const [playError] = useSound(ErrorSoundFile);
  const userType = Number(localStorage.getItem("user_type") || 7);

  // =========================
  // 🔹 States
  // =========================
  const [loading, setLoading] = useState(false);
  const [holding, setHolding] = useState([]);
  const [holdingSearch, setHoldingSearch] = useState([]);

  const [totals, setTotals] = useState({
    total: 0,
    buy: 0,
    sell: 0,
    nse: 0,
    nsebuy: 0,
    nsesell: 0,
    nsecash: 0,
    nsecashbuy: 0,
    nsecashsell: 0,
    mcx: 0,
    mcxbuy: 0,
    mcxsell: 0,
    options: 0,
    optionsbuy: 0,
    optionssell: 0,
    comex: 0,
    comexbuy: 0,
    comexsell: 0,
    crypto: 0,
    cryptobuy: 0,
    cryptosell: 0,
    forex: 0,
    forexbuy: 0,
    forexsell: 0,
  });

  const [activeExchanges, setActiveExchanges] = useState({
    nse: 0,
    nsecash: 0,
    mcx: 0,
    options: 0,
    comex: 0,
    crypto: 0,
    forex: 0,
  });

  const [reload, setReload] = useState(false);

  // =========================
  // 🔹 Fetch Holdings
  // =========================
  const loadHolding = useCallback(async () => {
    setLoading(true);
    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    };

    try {
      let response;
      if (userType === 1) response = await backend.holding_admin(data);
      else if (userType === 2) response = await backend.holding_super(data);
      else if (userType === 3) response = await backend.holding_master(data);
      else if (userType === 5) response = await backend.holding_broker(data);
      else throw new Error("Invalid user type");

      setLoading(false);

      if (response.error === "False") {
        dispatch({ type: "CLEAR_PF" });

        setHolding(response.holding);
        setHoldingSearch(response.holding);

        setTotals({
          total: response.total,

          buy: response.buy,
          sell: response.sell,

          nse: response.nse,
          nsebuy: response.nsebuy,
          nsesell: response.nsesell,

          nsecash: response.nsecash,
          nsecashbuy: response.nsecashbuy,
          nsecashsell: response.nsecashsell,

          mcx: response.mcx,
          mcxbuy: response.mcxbuy,
          mcxsell: response.mcxsell,

          options: response.options,
          optionsbuy: response.optionsbuy,
          optionssell: response.optionssell,

          comex: response.comex,
          comexbuy: response.comexbuy,
          comexsell: response.comexsell,

          crypto: response.crypto,
          cryptobuy: response.cryptobuy,
          cryptosell: response.cryptosell,

          forex: response.forex,
          forexbuy: response.forexbuy,
          forexsell: response.forexsell,
        });

        setActiveExchanges({
          nse: response.nseactive,
          nsecash: response.nsecashactive,
          mcx: response.mcxactive,
          options: response.optionsactive,
          comex: response.comexactive,
          crypto: response.cryptoactive,
          forex: response.forexactive,
        });
      } else {
        if (response.logout === 1) window.LogOut();
        else {
          playError();
          toast.error(response.message);
        }
      }
    } catch (err) {
      console.error("Load Holding Error:", err);
      setLoading(false);
      toast.error("Something went wrong while loading holdings.");
    }
  }, [userType]);

  useEffect(() => {
    loadHolding();
  }, [loadHolding]);

  // =========================
  // 🔹 Search Filter
  // =========================
  const search = (term) => {
    if (term.length > 1) {
      const filtered = holdingSearch.filter((x) =>
        x.symbol.toLowerCase().includes(term.toLowerCase())
      );
      setHolding(filtered);
    } else {
      setHolding(holdingSearch);
    }
  };

  // =========================
  // 🔹 Exchange Filter
  // =========================
  const FilterExchange = (type) => {
    setReload((prev) => !prev);
    dispatch({ type: "CLEAR_PF" });

    if (type === "TOTAL") return setHolding(holdingSearch);

    const map = {
      NSECASH: "nsecash",
      NSE: "nse",
      MCX: "mcx",
      OPTIONS: "fo",
      COMEX: "comex_future",
      CRYPTO: "crypto_future",
      FOREX: "forex_future",
    };
    const match = map[type];
    setHolding(
      match ? holdingSearch.filter((x) => x.type === match) : holdingSearch
    );
  };

  // =========================
  // 🔹 UI Rendering
  // =========================
  return (
    <div className="HoldingWrapper">
      <Filter search={search} records={holding.length} />

      <TopCard
        {...totals}
        {...activeExchanges}
        FilterExchange={FilterExchange}
      />

      <Menu />

      <div
        key={reload}
        style={{
          width: windowSize[0],
          height: windowSize[1] - 240,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <div className="loadingContainer">
            <Loading height={windowSize[1] - 240} />
          </div>
        ) : holding.length > 0 ? (
          <div
            style={{
              width: windowSize[0],
              height: windowSize[1] - 240,
              overflowY: "scroll",
            }}
          >
            {holding.map((item, index) => (
              <HoldingCard
                key={item.id}
                item={item}
                index={index + 1}
                reload={loadHolding}
                total={holding.length}
              />
            ))}
          </div>
        ) : (
          <NotFound
            height={windowSize[1] - 240}
            title="Nothing here"
            subtext="No Holding Found."
          />
        )}
      </div>
    </div>
  );
}
